import type { ComponentProps, CSSProperties, ReactNode, Ref } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import SearchForm from 'components/Search/SearchForm'
import type { SearchFormRef } from 'components/Search/SearchForm'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useScrollDirection from 'hooks/useScrollDirection'
import { useInView } from 'react-intersection-observer'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import HotelSearchPageFilters from './HotelSearchPageFilters'
import type { OfferListSortOption } from 'constants/offerListFilters'
import zIndex from 'styles/tools/z-index'
import { ANYWHERE_PLACE_ID, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { connect } from 'react-redux'
import AnywhereFilterTabs from './AnywhereFilterTabs'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { SearchMenuStates } from 'components/Search/type'
import HotelExposedFiltersHeader from './HotelExposedFiltersHeader/HotelExposedFiltersHeader'
import SearchField from './SearchField'

const SearchFormContainer = styled(LayoutContainer)`
  padding: ${rem(8)};
  display: none;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    display: block;
  }

  &.map-view {
    ${mediaQueryOnly.tablet} {
      display: none;
    }
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(32)};
  }

  &.edit-mode {
    display: block;
  }
`

const Wrapper = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin-top: ${rem(8)};
`

const TabsContainer = styled(LayoutContainer)`
  padding: ${rem(8)} 0 ${rem(8)} ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  ${mediaQueryUp.tablet} {
    border: none;
    padding-top: ${rem(16)};
    padding-bottom: ${rem(16)};
  }
`

const StyledPane = styled(Pane)`
  position: sticky;
  transition: top 1s ease-in-out;
  z-index: ${zIndex.searchMenu};

  &.alwaysSticky {
    top: 0;
    z-index: ${zIndex.stickySubHeader};
    transition: none;
  }

  &.offScreen:not(.alwaysSticky) {
    width: 100%;
    top: -50%;
    &:not(.mapPage) {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
    transition: top 1s ease-in-out;
  }
  &.lifted:not(.alwaysSticky) {
    top: 0;
    transition: top 0.5s ease-out;
    z-index: ${zIndex.stickySubHeader};
    &:not(.mapPage) {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
  }
`

const FilterContainer = styled.div`
  padding: ${rem(8)} ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const SENTINEL_STYLE: CSSProperties = { display: 'none' }

interface MappedStateProps {
  windowSearch: string
  pathname: string
}

type InheritedSearchFieldProps = Pick<ComponentProps<typeof SearchField>, 'onSortChange' | 'onFilterChange'>

interface Props extends InheritedSearchFieldProps {
  filters: App.OfferListFilters
  formRef: Ref<SearchFormRef>
  offerListFiltersProps: {
    searchType: App.SearchItem['searchType']
    switchView: {
      label: string
      view: 'map' | 'list'
      URL: string
      goBack?: boolean
    }
    sortOptions?: Array<OfferListSortOption>
    listFilters: App.OfferListFilters
  }
  searchVertical: App.SearchVertical
  baseSearchPath: string
  baseSearchMapPath: string
  disableAnywhereFilterTabs?: boolean
  locationSearchPlaceholder?: string
  typeaheadTypes?: Array<App.SearchPlaceType>
  showFilters?: boolean
  label?: string
  showNaturalLanguageSearchSuggestions?: boolean
  useMapLayout?: boolean
  mapLayoutFilterRow?: ReactNode
  alwaysSticky?: boolean
  onPaneHeightChange?: (height: number) => void
}

function HotelSearchHeader(props: Props & MappedStateProps) {
  const locationLabels = useLocationSearchLabels()
  const {
    formRef,
    filters,
    offerListFiltersProps: { searchType, switchView, sortOptions, listFilters },
    windowSearch,
    searchVertical,
    baseSearchPath,
    baseSearchMapPath,
    disableAnywhereFilterTabs,
    showFilters = true,
    locationSearchPlaceholder,
    typeaheadTypes = HOTEL_SEARCH_TYPEAHEAD_TYPES,
    pathname,
    onFilterChange,
    onSortChange,
    label = locationLabels.titleLabel,
    showNaturalLanguageSearchSuggestions = false,
    useMapLayout = false,
    mapLayoutFilterRow,
    alwaysSticky = false,
    onPaneHeightChange,
  } = props

  const {
    isEditMode,
    activeMenu,
  } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()

  const openSearchMenu = useCallback(() => { toggleSearchVertical(searchVertical) }, [toggleSearchVertical, searchVertical])

  const scrollDirection = useScrollDirection({ tolerance: 20 })

  const [toLiftRef, liftInView] = useInView()

  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])

  const isAnywhereSearch = params.destinationId === ANYWHERE_PLACE_ID

  useEffect(() => {
    if (isEditMode) {
      window.scrollTo(0, 0)
    }
  }, [isEditMode])

  useEffect(() => {
    if (!liftInView && isEditMode && scrollDirection === 'down') {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_EDIT_MODE,
        isEditMode: false,
      })
    }
  }, [liftInView, isEditMode, scrollDirection, globalSearchDispatch])

  const onSearch = useCallback(() => {
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_EDIT_MODE,
      isEditMode: false,
    })
  }, [globalSearchDispatch])

  const paneElementRef = useRef<HTMLElement | null>(null)
  const paneSentinelRef = useCallback((node: HTMLDivElement | null) => {
    paneElementRef.current = node?.parentElement ?? null
  }, [])

  useEffect(() => {
    if (!onPaneHeightChange) return
    const pane = paneElementRef.current
    if (!pane) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height
      onPaneHeightChange(height)
    })
    observer.observe(pane)
    return () => observer.disconnect()
  }, [onPaneHeightChange])

  const isLifted = !liftInView && scrollDirection === 'up'
  const isOffScreen = !liftInView && scrollDirection !== 'up'

  const isMobileScreen = useScreenSizeOnly('mobile')
  const isTabletScreen = useScreenSizeOnly('tablet')

  const isMapPage = pathname.includes('map')
  const isMapLayout = isMapPage || useMapLayout
  const showCompactSearchField = isMobileScreen || (isMapPage && isTabletScreen)

  const searchFormElement = <SearchForm
    submitOnApply
    filters={filters}
    ref={formRef}
    onSubmit={onSearch}
    typeaheadTypes={typeaheadTypes}
    switchView={switchView}
    baseSearchPath={baseSearchPath}
    baseSearchMapPath={baseSearchMapPath}
    locationSearchPlaceholder={locationSearchPlaceholder}
    label={label}
    showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
    useMapLayout={useMapLayout}
  />

  const mapLayoutFilters = useMemo(() => {
    if (mapLayoutFilterRow) {
      return <FilterContainer>{mapLayoutFilterRow}</FilterContainer>
    }

    if (showFilters) {
      return (
        <FilterContainer>
          <HotelExposedFiltersHeader filters={listFilters} onFilterChange={onFilterChange} onSortChange={onSortChange} />
        </FilterContainer>
      )
    }

    return null
  }, [listFilters, mapLayoutFilterRow, onFilterChange, onSortChange, showFilters])

  return <>
    <StyledPane
      className={clsx({
        alwaysSticky,
        lifted: !alwaysSticky && (isLifted || activeMenu !== SearchMenuStates.Closed),
        offScreen: !alwaysSticky && isOffScreen && activeMenu === SearchMenuStates.Closed,
        mapPage: isMapPage,
      })}
      shadow={!isMapLayout ? 'bottom-small' : undefined}
    >
      {onPaneHeightChange && <div ref={paneSentinelRef} style={SENTINEL_STYLE} />}
      {showCompactSearchField &&
        <SearchField
          onEdit={openSearchMenu}
          filters={listFilters}
          sortOptions={sortOptions}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          showFilters={isMapLayout ? false : showFilters}
          isMapView={isMapPage}
          switchView={switchView}
        />}
      {isMapLayout &&
        <>
          <SearchFormContainer as="div" className="map-view">
            {searchFormElement}
          </SearchFormContainer>
          {mapLayoutFilters}
        </>}
      {!isMapLayout &&
        <SearchFormContainer className={clsx({
          'edit-mode': isEditMode && liftInView,
        })}
        >
          {searchFormElement}
        </SearchFormContainer>}
      {!isMobileScreen && isAnywhereSearch && !isMapLayout && !disableAnywhereFilterTabs && <Wrapper>
        <TabsContainer>
          <AnywhereFilterTabs
            filters={listFilters}
            search={windowSearch}
          />
        </TabsContainer>
      </Wrapper>}
      {searchType !== 'property' && isMapPage &&
        <HotelSearchPageFilters
          filters={listFilters}
          sortOptions={sortOptions}
          switchView={isMobileScreen ? undefined : switchView}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
        />}
      {isMobileScreen && isAnywhereSearch && !isMapPage && !disableAnywhereFilterTabs && <TabsContainer>
        <AnywhereFilterTabs filters={listFilters} search={windowSearch} />
      </TabsContainer>}
    </StyledPane>
    <div ref={toLiftRef} />
  </>
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState): MappedStateProps => ({
  windowSearch: appState.router.location.search,
  pathname: appState.router.location.pathname,
}))(HotelSearchHeader)
